import React, { useState } from "react";
import { X, Loader2, DollarSign, Ban, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { refundsApi, centsToAUD } from "../lib/events";
import { extractError } from "../lib/api";

/**
 * RefundModal — organiser refund UI for a single order.
 * - Free orders show a Void tickets-only flow.
 * - Paid orders show a full-vs-partial toggle + amount input + reason + void checkbox.
 */
export function RefundModal({ order, onClose, onRefunded }) {
  const isFree = (order?.amount_total_cents || 0) === 0;
  const remainingCents = Math.max(0, (order?.amount_total_cents || 0) - (order?.refunded_cents || 0));
  const [mode, setMode] = useState("full"); // 'full' | 'partial'
  const [amount, setAmount] = useState((remainingCents / 100).toFixed(2));
  const [reason, setReason] = useState("");
  const [voidAll, setVoidAll] = useState(true);
  const [busy, setBusy] = useState(false);
  // Sprint T2 — partial refunds can select add-on (Extras) lines
  const addonLines = (order?.addon_items || []).filter((a) => !a.refunded);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const selectedAddonTotal = addonLines
    .filter((a) => selectedAddonIds.includes(a.addon_id))
    .reduce((s, a) => s + (a.line_total_cents || 0), 0);

  const toggleAddon = (id) => {
    setSelectedAddonIds((prev) => {
      const checking = !prev.includes(id);
      const next = checking ? [...prev, id] : prev.filter((x) => x !== id);
      const total = addonLines
        .filter((a) => next.includes(a.addon_id))
        .reduce((s, a) => s + (a.line_total_cents || 0), 0);
      // Snap the amount to the exact selected extras total on every toggle —
      // prevents accidentally refunding the full remaining balance when the
      // organiser only intends to refund specific extras. If nothing remains
      // selected, restore the remaining-balance default.
      setAmount(((total > 0 ? total : remainingCents) / 100).toFixed(2));
      return next;
    });
  };

  if (!order) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { reason: reason.trim() || undefined, void_all_tickets: voidAll };
      if (!isFree && mode === "partial") {
        const cents = Math.round(parseFloat(amount || "0") * 100);
        if (!(cents > 0)) throw new Error("Enter an amount greater than $0.00.");
        if (cents > remainingCents) throw new Error("Amount exceeds remaining balance.");
        if (selectedAddonIds.length && cents < selectedAddonTotal) {
          throw new Error("Amount must cover the selected extras total.");
        }
        payload.amount_cents = cents;
        if (selectedAddonIds.length) payload.refund_addon_item_ids = selectedAddonIds;
      }
      const res = await refundsApi.refundOrder(order.id, payload);
      toast.success(isFree ? "Tickets voided." : "Refund processed.");
      onRefunded?.(res);
      onClose?.();
    } catch (err) {
      toast.error(extractError(err) || err?.message || "Refund failed.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="refund-modal"
    >
      <div
        className="bg-white border-2 border-[var(--gt-ink)] shadow-[8px_8px_0px_0px_var(--gt-ink)] w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-[var(--gt-ink)]">
          <div>
            <div className="gt-label">{isFree ? "Void tickets" : "Refund order"}</div>
            <h3 className="font-heading text-2xl font-black tracking-tighter mt-1">
              {order.buyer_name} · {order.buyer_email}
            </h3>
            <div className="text-xs text-[var(--gt-ink-3)] mt-1 font-mono">Order {order.id.slice(0, 8)}</div>
          </div>
          <button onClick={onClose} className="text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]" data-testid="refund-modal-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-5" data-testid="refund-form">
          {isFree ? (
            <div className="border-2 border-dashed border-[var(--gt-line)] p-4 text-sm text-[var(--gt-ink-2)]">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-[var(--gt-accent)] mt-0.5" />
                <div>
                  This is a <strong>free order</strong> — no payment was taken.
                  Continuing will void every ticket in this order; QR scans at the door will fail.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="gt-card !p-3">
                  <div className="gt-label mb-1">Original</div>
                  <div className="font-mono font-bold">{centsToAUD(order.amount_total_cents)}</div>
                </div>
                <div className="gt-card !p-3">
                  <div className="gt-label mb-1">Refunded</div>
                  <div className="font-mono font-bold">{centsToAUD(order.refunded_cents || 0)}</div>
                </div>
                <div className="gt-card !p-3">
                  <div className="gt-label mb-1">Remaining</div>
                  <div className="font-mono font-bold text-[var(--gt-accent)]" data-testid="refund-remaining">{centsToAUD(remainingCents)}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setMode("full")}
                        className={`gt-btn-ghost text-sm ${mode === "full" ? "!bg-[var(--gt-ink)] !text-white" : ""}`}
                        data-testid="refund-mode-full">
                  Full refund
                </button>
                <button type="button" onClick={() => setMode("partial")}
                        className={`gt-btn-ghost text-sm ${mode === "partial" ? "!bg-[var(--gt-ink)] !text-white" : ""}`}
                        data-testid="refund-mode-partial">
                  Partial refund
                </button>
              </div>

              {mode === "partial" && (
                <div className="space-y-4">
                  {addonLines.length > 0 && (
                    <div data-testid="refund-addon-selector">
                      <div className="gt-form-label">Refund extras (optional)</div>
                      <div className="space-y-1.5 border border-[var(--gt-line)] rounded-sm p-3">
                        {addonLines.map((a) => (
                          <label key={a.addon_id} className="flex items-center justify-between gap-2 text-sm cursor-pointer">
                            <span className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedAddonIds.includes(a.addon_id)}
                                onChange={() => toggleAddon(a.addon_id)}
                                data-testid={`refund-addon-check-${a.addon_id}`}
                              />
                              {a.name}
                              {a.category === "donation" && (
                                <span className="text-[9px] uppercase tracking-widest px-1 py-0.5 border border-[var(--gt-line)] rounded-sm text-[var(--gt-ink-3)]">Donation</span>
                              )}
                              <span className="text-xs text-[var(--gt-ink-3)]">× {a.quantity}</span>
                            </span>
                            <span className="font-mono font-bold text-xs">{centsToAUD(a.line_total_cents || 0)}</span>
                          </label>
                        ))}
                        {selectedAddonTotal > 0 && (
                          <div className="pt-1.5 border-t border-[var(--gt-line)] flex justify-between text-xs">
                            <span className="text-[var(--gt-ink-3)]">Selected extras total (inventory restored where applicable)</span>
                            <span className="font-mono font-bold" data-testid="refund-addon-selected-total">{centsToAUD(selectedAddonTotal)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="gt-form-label">Amount (AUD)</label>
                    <div className="relative">
                      <DollarSign size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
                      <input
                        className="gt-input pl-7"
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={(remainingCents / 100).toFixed(2)}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        data-testid="refund-amount-input"
                      />
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
                      Max {centsToAUD(remainingCents)}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="gt-form-label">Reason <span className="text-[var(--gt-ink-3)] font-normal">(optional, permanently logged)</span></label>
            <textarea
              className="gt-input"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. customer requested cancellation"
              data-testid="refund-reason-input"
            />
          </div>

          {!isFree && (
            <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="refund-void-toggle">
              <input
                type="checkbox"
                checked={voidAll}
                onChange={(e) => setVoidAll(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="font-semibold">Void all tickets in this order</span>
                <div className="text-xs text-[var(--gt-ink-3)]">Recommended for full refunds. Voided tickets fail at the door scan.</div>
              </span>
            </label>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="gt-btn-ghost text-sm" data-testid="refund-cancel-btn">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              className={`text-sm ${isFree ? "gt-btn-primary" : "gt-btn-primary"}`}
              data-testid="refund-submit-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : isFree ? <Ban size={14} /> : <DollarSign size={14} />}
              {isFree ? "Void tickets" : (mode === "full" ? "Refund full amount" : "Refund amount")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
